import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Trash2, User, Mail, Lock, Save, ShieldCheck, AtSign } from "lucide-react";

const ALLOWED = ["image/png", "image/jpeg", "image/webp"];
const MAX_BYTES = 1 * 1024 * 1024;

export default function Profile() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [title, setTitle] = useState(user?.title || "");
  const [avatar, setAvatar] = useState(user?.avatar_data || "");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [next2, setNext2] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  // email change
  const [newEmail, setNewEmail] = useState("");
  const [emailPw, setEmailPw] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  useEffect(() => {
    setName(user?.name || "");
    setTitle(user?.title || "");
    setAvatar(user?.avatar_data || "");
  }, [user]);

  const initials = (user?.name || user?.email || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  const saveProfile = async () => {
    setBusy(true);
    try {
      await api.patch("/users/me", { name, title });
      toast.success("Profile updated");
      await refresh();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) { toast.error("PNG, JPEG or WebP only"); return; }
    if (f.size > MAX_BYTES) { toast.error(`Too large (${(f.size / 1024 / 1024).toFixed(2)} MB). Max 1 MB.`); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/users/me/avatar", form, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatar(data.avatar_data);
      toast.success("Photo updated");
      await refresh();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeAvatar = async () => {
    setUploading(true);
    try {
      await api.delete("/users/me/avatar");
      setAvatar("");
      toast.success("Photo removed");
      await refresh();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (next !== next2) { toast.error("New passwords do not match"); return; }
    setPwBusy(true);
    try {
      await api.post("/users/me/password", { current_password: current, new_password: next });
      toast.success("Password changed");
      setCurrent(""); setNext(""); setNext2("");
    } catch (err) { toast.error(apiError(err)); }
    finally { setPwBusy(false); }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    setEmailBusy(true);
    try {
      const { data } = await api.post("/users/me/email", { current_password: emailPw, new_email: newEmail });
      toast.success(`Email updated to ${data.email} — use it on your next sign-in`);
      setNewEmail(""); setEmailPw("");
      await refresh();
    } catch (err) { toast.error(apiError(err)); }
    finally { setEmailBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="profile-page">
      <PageHeader kicker="Profile" title="Your account" description="Update your name, photo, login email and password." />

      {/* Identity */}
      <Card className="p-6 mb-5" data-testid="profile-identity-card">
        <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold mb-4">Identity</div>
        <div className="flex items-start gap-6">
          <div className="shrink-0">
            <div className="h-24 w-24 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center text-slate-500 text-2xl font-semibold border-2 border-white shadow-md">
              {avatar ? <img src={avatar} alt="" className="h-full w-full object-cover" data-testid="profile-avatar-preview" /> : initials}
            </div>
            <input ref={fileRef} type="file" accept={ALLOWED.join(",")} onChange={onFile} className="hidden" data-testid="profile-avatar-input" />
            <div className="flex flex-col gap-1.5 mt-3">
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="profile-avatar-upload">
                <Upload className="h-3.5 w-3.5 mr-1.5" /> {uploading ? "Uploading…" : "Change photo"}
              </Button>
              {avatar && (
                <Button type="button" variant="ghost" size="sm" onClick={removeAvatar} disabled={uploading} className="text-rose-600 hover:text-rose-700" data-testid="profile-avatar-remove">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
                </Button>
              )}
            </div>
          </div>
          <div className="flex-1 space-y-4">
            <div>
              <Label htmlFor="p-name" className="flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-slate-400" /> Full name</Label>
              <Input id="p-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
            </div>
            <div>
              <Label htmlFor="p-title">Job title <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input id="p-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Chief Risk Officer" data-testid="profile-title" />
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-slate-400" /> Email <span className="text-slate-400 font-normal">(login — change below)</span></Label>
              <Input value={user?.email || ""} disabled className="bg-slate-50 font-mono text-xs" data-testid="profile-email" />
            </div>
            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Role: <span className="font-mono font-semibold text-slate-800">{user?.role}</span>
              </div>
              <Button onClick={saveProfile} disabled={busy} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="profile-save">
                <Save className="h-3.5 w-3.5 mr-1.5" /> {busy ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Email */}
      <Card className="p-6 mb-5" data-testid="profile-email-card">
        <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold mb-4 flex items-center gap-1.5">
          <AtSign className="h-3.5 w-3.5" /> Change login email
        </div>
        <form onSubmit={changeEmail} className="space-y-4 max-w-md">
          <div>
            <Label htmlFor="p-new-email">New email address</Label>
            <Input id="p-new-email" type="email" autoComplete="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder={user?.email || "you@company.com"} required data-testid="profile-email-new" />
            <div className="text-[11px] text-slate-500 mt-1">You'll use this address to sign in next time. Your session stays active until you sign out.</div>
          </div>
          <div>
            <Label htmlFor="p-email-cur">Current password <span className="text-slate-400 font-normal">(to confirm it's you)</span></Label>
            <Input id="p-email-cur" type="password" autoComplete="current-password" value={emailPw} onChange={(e) => setEmailPw(e.target.value)} required data-testid="profile-email-pw" />
          </div>
          <Button type="submit" disabled={emailBusy || !newEmail || !emailPw || newEmail.toLowerCase() === (user?.email || "").toLowerCase()} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="profile-email-submit">
            {emailBusy ? "Updating…" : "Update email"}
          </Button>
        </form>
      </Card>

      {/* Password */}
      <Card className="p-6" data-testid="profile-password-card">
        <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold mb-4 flex items-center gap-1.5">
          <Lock className="h-3.5 w-3.5" /> Change password
        </div>
        <form onSubmit={changePw} className="space-y-4 max-w-md">
          <div>
            <Label htmlFor="p-cur">Current password</Label>
            <Input id="p-cur" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="profile-pw-current" />
          </div>
          <div>
            <Label htmlFor="p-new">New password</Label>
            <Input id="p-new" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required data-testid="profile-pw-new" />
            <div className="text-[11px] text-slate-500 mt-1">At least 12 characters, mixed case, number & symbol.</div>
          </div>
          <div>
            <Label htmlFor="p-new2">Confirm new password</Label>
            <Input id="p-new2" type="password" autoComplete="new-password" value={next2} onChange={(e) => setNext2(e.target.value)} required data-testid="profile-pw-confirm" />
          </div>
          <Button type="submit" disabled={pwBusy || !current || !next || next !== next2} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="profile-pw-submit">
            {pwBusy ? "Changing…" : "Change password"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
